import React from 'react';
import { ModalLayout, Content, Text } from 'components';
import { Avatar, Button, Flex, InputNumber, Select } from 'antd';
import MessageAlert, { stringToMoneyFormat, STORE_URL } from 'utils';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

const LABEL = {
  REQUIRED: '필수옵션',
  ADDITION: '추가옵션',
};

const ProductModal = (props) => {
  /* ===== Route ===== */
  /* ===== Props ===== */
  const { data = null, onSubmit = () => { }, onUpdate = () => { }, onClose = () => { } } = props;

  /* ===== State ===== */
  // 상품 데이터
  const [product, setProduct] = React.useState(null);
  // 모달 열림 여부
  const [isOpened, setIsOpened] = React.useState(false);
  // 장바구니 담을 수량
  const [cartNum, setCartNum] = React.useState(1);
  // 상품 옵션
  const [productOptions, setProductOptions] = React.useState(null);
  // 옵션 금액
  const [optionPrice, setOptionPrice] = React.useState(0);

  /* ===== Hooks ===== */

  /* ===== Function ===== */
  /**
   * ANCHOR 옵션 선택
   * --
   * @param {object} option
   */
  const handleSelectOption = (option) => {
    const { type, id, option_key } = option;

    setProductOptions((prev) => ({
      ...prev,
      [type]: prev[type].map((item) =>
        item.option_id !== id
          ? item
          : {
            ...item,
            option: item.option?.map((op) =>
              op?.option_key !== option_key
                ? { ...op, selected: false }
                : { ...op, selected: true }
            ),
          }
      ),
    }));
  };

  /**
   * ANCHOR 장바구니 추가
   * --
   */
  const handleAddCart = () => {
    let productKey = `${product.product_id}`;
    let productOption = null;

    // 옵션이 있는 상품 일 경우 필수 옵션 모두 선택했는지 체크
    if (productOptions) {
      const { REQUIRED } = productOptions;

      const isExist = REQUIRED.every((item) =>
        item.option.some((sub) => sub.selected)
      );

      if (!isExist)
        return MessageAlert.warning('필수 옵션을 모두 선택해주세요!');

      productOption = [
        ...productOptions['REQUIRED'],
        ...productOptions['ADDITION'],
      ];
      productKey = productOption.reduce((acc, cur) => {
        const selectedOptionKey = cur.option.find((item) => item.selected);
        const optionKey = `/${cur.option_id}-${selectedOptionKey?.option_key ? selectedOptionKey.option_key : "not_selected"}`;

        acc += optionKey;

        return acc;
      }, productKey);
    }

    const cartProduct = {
      product_key: productKey,
      discount_price: 0,
      reserve_price: 0,
      commission_rate: 0,
      commission_type: 'R',
      ...product,
      product_option: productOption,
      num: cartNum,
      option_price: optionPrice,
    };

    if (product?.product_key) {
      onUpdate &&
        onUpdate({ ...cartProduct, origin_key: product?.product_key }, () => {
          setIsOpened(!isOpened);
          MessageAlert.success('수정되었습니다.');
        });
    } else {
      onSubmit &&
        onSubmit(cartProduct, () => {
          setIsOpened(!isOpened);
          MessageAlert.success('목록에 추가되었습니다.');
        });
    }
  };

  /* ===== Variable ===== */
  /* ===== Hooks ===== */
  /**
   * ANCHOR 데이터 state 저장
   * --
   */
  React.useEffect(() => {
    if (data) {
      setProduct(data);
      setIsOpened(true);

      if (data?.product_key) {
        setCartNum(data?.num ?? 1);
      }
    }
  }, [data]);

  /**
   * ANCHOR 상품 옵션 추출
   * --
   */
  React.useEffect(() => {
    if (!product) return;

    const { product_option = null } = product;

    if (!product_option) {
      setProductOptions(null);
    } else {
      const classification = product_option?.reduce(
        (acc, cur) => {
          if (cur.type in acc) {
            acc[cur.type].push(cur);
          }
          return acc;
        },
        { REQUIRED: [], ADDITION: [] }
      );

      ['REQUIRED', 'ADDITION'].forEach((key) => {
        classification[key].sort((a, b) => a.order - b.order);
      });

      setProductOptions(classification);
    }
  }, [product]);

  /**
   * ANCHOR 옵션금
   * --
   */
  React.useEffect(() => {
    if (!productOptions) {
      setOptionPrice(0);
    } else {
      const totalOptionPrice = Object.values(productOptions).reduce(
        (total, options) => {
          total += options.reduce((acc, cur) => {
            const selected = cur.option.find((op) => op?.selected) ?? null;

            if (selected) {
              acc += Number(selected?.option_price);
            }

            return acc;
          }, 0);

          return total;
        },
        0
      );

      setOptionPrice(Number(totalOptionPrice));
    }
  }, [productOptions]);

  /* ===== Render ===== */
  return (
    <ModalLayout
      open={isOpened}
      onCancel={() => {
        onClose();
        setIsOpened(false);
      }}
      afterClose={() => {
        setProduct(null);
        setCartNum(1);
      }}
      closable
      title={product?.product_name}
      width={500}
      footer={
        <Flex gap={12}>
          <Button
            size="large"
            style={{ flex: 1 }}
            onClick={() => {
              onClose();
              setIsOpened(false);
            }}
          >
            닫기
          </Button>
          <Button
            type="primary"
            size="large"
            style={{ flex: 1 }}
            onClick={() => handleAddCart()}
          >
            담기
          </Button>
        </Flex>
      }
    >
      <Content maxWidth={'100%'}>
        <Flex vertical gap={24}>
          {/* SECTION 상품 정보 */}
          <Flex gap={8} align="center">
            <Avatar
              src={`${STORE_URL}/${product?.thumbnail?.file_url}`}
              size={84}
              shape="square"
            />

            <Flex vertical gap={4}>
              <Text size={12} color={'#666666'}>
                {product?.shop_information?.shop_information_name}
              </Text>
              <Text weight={700} color={'#111111'}>
                {product?.product_name}
              </Text>

              <Text size={14} weight={600}>
                {stringToMoneyFormat(product?.price, ' 원')}
              </Text>
            </Flex>
          </Flex>
          {/* !SECTION 상품 정보 */}

          {/* SECTION: 옵션 */}
          {!productOptions ? null : (
            <Flex vertical>
              {Object.entries(productOptions).map(([key, value]) =>
                !value.length ? null : (
                  <Flex vertical gap={2}>
                    <Text size={12} color={'#999999'}>
                      {LABEL[key]}
                    </Text>

                    <Flex vertical gap={4}>
                      {value?.map((item) => (
                        <Select
                          size="large"
                          placeholder={item?.option_title}
                          value={
                            item?.option?.find((option) => option?.selected)
                              ?.option_key ?? null
                          }
                          options={item?.option?.map((option) => ({
                            ...option,
                            value: option?.option_key,
                            label: `${option?.option_name
                              } (+ ${stringToMoneyFormat(
                                option?.option_price,
                                '원'
                              )})`,
                          }))}
                          onChange={(_, option) =>
                            handleSelectOption({
                              type: item.type,
                              id: item.option_id,
                              ...option,
                            })
                          }
                        />
                      ))}
                    </Flex>
                  </Flex>
                )
              )}
            </Flex>
          )}
          {/* !SECTION: 옵션 */}

          {/* SECTION: 수량 */}
          <Flex align="center" justify="space-between">
            <Text weight={700}>수량</Text>

            <InputNumber
              size="large"
              min={1}
              max={99}
              value={cartNum}
              onChange={(e) => setCartNum(e)}
              controls={false}
              addonBefore={
                <Button
                  size="small"
                  icon={<MinusOutlined />}
                  type="icon"
                  onClick={() => {
                    if (cartNum === 1)
                      return MessageAlert.info(
                        '최소 1개 이상의 수량이 입력되어야합니다.'
                      );

                    setCartNum((prev) => --prev);
                  }}
                />
              }
              addonAfter={
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  type="icon"
                  onClick={() => {
                    if (cartNum === 99)
                      return MessageAlert.info(
                        '최대 99개의 수량까지 입력 가능합니다.'
                      );

                    setCartNum((prev) => ++prev);
                  }}
                />
              }
              style={{ width: 160 }}
            />
          </Flex>
          {/* !SECTION: 수량 */}
        </Flex>
      </Content>
    </ModalLayout>
  );
};

export default ProductModal;
